import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";

import API_URL from "../../api/API";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";

const WaiverReturningEditor = () => {

    const { business } = useBusinessAuthContext();

    const {
        waiver,refreshChangeSummary,setSaveHandler,setHasUnsavedChanges,
    } = useOutletContext();

    const waiverId = waiver?._id;

    const [returningSignerPolicy, setReturningSignerPolicy] = useState(null);

    const [saving, setSaving] = useState(false);

    const [isDirty, setIsDirty] = useState(false);

    
    /*
    ----------------------------------------------------------
    Save Draft
    ----------------------------------------------------------
    */

    const saveDraft = useCallback(async () => { ////////NEW 6th July 2026
    
        if (saving) return false;

        const validPolicies = ["STANDARD_RESIGN", "QUICK_RESIGN"];
        if (!validPolicies.includes(returningSignerPolicy)) {
            return false;
        }

        setSaving(true);

        try {

            const res = await fetch(

                `${API_URL.BUSINESS_WAIVER_SMART_STEP_RETURNING_POLICY}/${waiverId}`,

                {

                    method: "PATCH",

                    headers: {

                        Authorization: `Bearer ${business.token}`,

                        "Content-Type": "application/json",

                        "x-session-id": business.sessionId,

                        "x-frontend-url": window.location.pathname,

                    },

                    body: JSON.stringify({

                        returningSignerPolicy,

                    }),

                }

            );

            const data = await res.json();

            if (!data.success) {

                alert(data.message);

                return false;

            }
            
            await refreshChangeSummary(); // refreshWaiver() must not be used, because it reloads changes overwritten here.
            
            return true;

        }
        catch (err) {

            console.error(err);

            return false;

        }
        finally {

            setSaving(false);

        }

   }, [
        waiverId,
        returningSignerPolicy,
        business,
        refreshChangeSummary,
        saving,
    ]); /* Otherwise it will will still recreate saveHandler every render unless saveDraft is stable. */
    

    /* Each tab registers its save function*/  ////////NEW 6th July 2026
    useEffect(() => {
        setSaveHandler(() => saveDraft);
        return () => setSaveHandler(null);
    }, []); /* STOP tracking saveDraft in dependencies - else infintie rendering loop in browser */ 
    
    useEffect(() => {
        setHasUnsavedChanges(isDirty);
    }, [isDirty]); /* setHasUnsavedChanges DO NOT include setter in deps (it’s stable anyway). */
   
    
    
    /*
    ----------------------------------------------------------
    Normalize Policy
    ----------------------------------------------------------
    */

    const normalizePolicy = (value) => {

        if (!value) return null;

        const v = String(value).trim().toLowerCase();

        if (v === "standard_resign") return "STANDARD_RESIGN";

        if (v === "quick_resign") return "QUICK_RESIGN";

        return null;

    };

    /*
    ----------------------------------------------------------
    Load Existing Value
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!waiver) return;

        setReturningSignerPolicy(

            normalizePolicy(
                waiver.returningSignerPolicy
            )

        );

    }, [waiver]);

    /*
    ----------------------------------------------------------
    Update Policy
    ----------------------------------------------------------
    */

    const updatePolicy = value => {

        if (value === returningSignerPolicy) return;

        setReturningSignerPolicy(value);

        setIsDirty(true);

    };

    /*
    ----------------------------------------------------------
    Auto Save

    Future Improvement

    This editor may eventually be changed to use an explicit
    "Save Changes" button instead of auto-save.

    Configuration editors generally benefit from users making
    all changes before committing them.

    Auto-save is currently used for consistency with the
    other waiver editors.
    ----------------------------------------------------------
    */

    useEffect(() => {

        if (!isDirty) return;

        const timer = setTimeout(() => {

            saveDraft();

            setIsDirty(false);
            setHasUnsavedChanges(false); ////////NEW 6th July 2026

        }, 1500);

        return () => clearTimeout(timer);

    }, [returningSignerPolicy, isDirty]);

    

    /*
    ----------------------------------------------------------
    Render
    ----------------------------------------------------------
    */

    return (

        <div className="max-w-3xl">

            <div className="bg-white rounded-xl shadow-sm p-8">

                <h2 className="text-lg font-semibold mb-1">

                    Returning Signers

                </h2>

                <p className="text-sm text-muted-foreground mb-6">

                    Configure how returning participants sign future waivers.

                </p>

                {/* Standard Re-Sign */}

                <button

                    type="button"

                    onClick={() =>
                        updatePolicy("STANDARD_RESIGN")
                    }

                    className={`w-full text-left border rounded-lg p-4 mb-4 transition-colors ${
                        returningSignerPolicy === "STANDARD_RESIGN"
                            ? "border-yellow-500 bg-yellow-50"
                            : "border-gray-200 hover:border-gray-300"
                    }`}

                >

                    <div className="font-medium">

                        Standard Re-Sign (Recommended)

                    </div>

                    <div className="text-sm text-muted-foreground mt-1">

                        Returning participants complete the waiver again
                        in full, ensuring updated liability coverage and
                        fresh consent.

                    </div>

                </button>

                {/* Quick Re-Sign */}

                <button

                    type="button"

                    onClick={() =>
                        updatePolicy("QUICK_RESIGN")
                    }

                    className={`w-full text-left border rounded-lg p-4 transition-colors ${
                        returningSignerPolicy === "QUICK_RESIGN"
                            ? "border-yellow-500 bg-yellow-50"
                            : "border-gray-200 hover:border-gray-300"
                    }`}

                >

                    <div className="font-medium">

                        Quick Re-Sign

                    </div>

                    <div className="text-sm text-muted-foreground mt-1">

                        Returning participants confirm their previous
                        waiver without completing all sections again.
                        Faster experience with slightly reduced liability
                        coverage.

                    </div>

                </button>

                {saving && (

                    <div className="text-sm text-gray-500 mt-6">

                        Saving...

                    </div>

                )}

                {/*
                ------------------------------------------------------
                Future Improvement

                Replace auto-save with a "Save Changes" button if this
                editor grows or requires users to review multiple
                configuration changes before saving.
                ------------------------------------------------------
                */}

            </div>

        </div>

    );

};

export default WaiverReturningEditor;